<template>
  <div
    id="register-modal"
    tabindex="-1"
    aria-hidden="true"
    class="hidden overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 p-4 w-full md:inset-0 h-modal md:h-full"
  >
    <div class="relative w-full max-w-md h-full md:h-auto">
      <div class="relative bg-white rounded-lg shadow dark:bg-gray-700">
        <button
          type="button"
          class="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white"
          @click="closeModal"
        >
          <svg
            aria-hidden="true"
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          ><path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          /></svg>
          <span class="sr-only">{{ $t('landing.register.close') }}</span>
        </button>
        <div class="py-6 px-6 lg:px-8">
          <h3 class="mb-4 text-xl font-medium text-gray-900 dark:text-white">
            {{ $t('landing.register.title') }}
          </h3>
          <form
            class="space-y-6"
            @submit.prevent="submit"
          >
            <div>
              <label
                for="registerEmail"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >{{ $t('landing.register.email') }}</label>
              <input
                id="registerEmail"
                type="email"
                autocomplete="email"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-400 focus:border-primary-400 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                placeholder="mail@example.com"
                required
              >
            </div>
            <div>
              <label
                for="registerPassword"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >{{ $t('landing.register.password') }}</label>
              <input
                id="registerPassword"
                type="password"
                placeholder="••••••••"
                minlength="8"
                autocomplete="new-password"
                data-popover-target="popover-registerPassword"
                data-popover-placement="bottom"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-400 focus:border-primary-400 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                required
                @keyup="passwordStrengthCheck"
              >
            </div>
            <div>
              <label
                for="password"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >{{ $t('landing.register.retypePassword') }}</label>
              <input
                id="registerRetypePassword"
                type="password"
                placeholder="••••••••"
                minlength="8"
                autocomplete="off"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-400 focus:border-primary-400 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                required
              >
            </div>
            <div>
              <button
                :disabled="loading"
                type="submit"
                class="mb-4 w-full text-white bg-primary-500 hover:bg-primary-600 focus:ring-4 focus:outline-none focus:ring-primary-400 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-primary-400"
              >
                <svg
                  v-if="loading"
                  role="status"
                  class="inline mr-3 w-4 h-4 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
                {{ $t('landing.register.register') }}
              </button>
              <div class="text-sm font-medium text-gray-500 dark:text-gray-300">
                {{ $t('landing.register.haveAccount') }} <a
                  class="text-primary-500 hover:underline dark:text-primary-400 cursor-pointer"
                  @click="openLoginModal"
                >{{ $t('landing.register.login') }}</a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      id="popover-registerPassword"
      data-popover
      role="tooltip"
      class="absolute z-10 invisible inline-block text-sm font-light text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-lg shadow-sm opacity-0 w-72 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
    >
      <div class="p-3 space-y-2 text-gray-900 dark:text-white">
        <h3 class="font-semibold">
          Must have at least 8 characters
        </h3>
        <div
          v-if="passwordStrength.score == 0"
          class="grid grid-cols-4 gap-2"
        >
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
        </div>
        <div
          v-if="passwordStrength.score == 1"
          class="grid grid-cols-4 gap-2"
        >
          <div class="h-1 bg-red-400 dark:bg-red-400" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
        </div>
        <div
          v-if="passwordStrength.score == 2"
          class="grid grid-cols-4 gap-2"
        >
          <div class="h-1 bg-red-400 dark:bg-red-400" />
          <div class="h-1 bg-red-400 dark:bg-red-400" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
        </div>
        <div
          v-if="passwordStrength.score == 3"
          class="grid grid-cols-4 gap-2"
        >
          <div class="h-1 bg-yellow-400 dark:bg-yellow-400" />
          <div class="h-1 bg-yellow-400 dark:bg-yellow-400" />
          <div class="h-1 bg-yellow-400 dark:bg-yellow-400" />
          <div class="h-1 bg-gray-200 dark:bg-gray-600" />
        </div>
        <div
          v-if="passwordStrength.score == 4"
          class="grid grid-cols-4 gap-2"
        >
          <div class="h-1 bg-primary-400 dark:bg-primary-400" />
          <div class="h-1 bg-primary-400 dark:bg-primary-400" />
          <div class="h-1 bg-primary-400 dark:bg-primary-400" />
          <div class="h-1 bg-primary-400 dark:bg-primary-400" />
        </div>
        <p>It’s better to have:</p>
        <ul>
          <li class="flex items-center mb-1">
            <svg
              v-if="passwordStrength.upperLower"
              class="w-4 h-4 mr-2 text-primary-300 dark:text-primary-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            /></svg>
            <svg
              v-else
              class="w-4 h-4 mr-2 text-gray-300 dark:text-gray-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            /></svg>
            Upper & lower case letters
          </li>
          <li class="flex items-center">
            <svg
              v-if="passwordStrength.number"
              class="w-4 h-4 mr-2 text-primary-300 dark:text-primary-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            /></svg>
            <svg
              v-else
              class="w-4 h-4 mr-2 text-gray-300 dark:text-gray-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            /></svg>
            A number
          </li>
          <li class="flex items-center">
            <svg
              v-if="passwordStrength.special"
              class="w-4 h-4 mr-2 text-primary-300 dark:text-primary-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            /></svg>
            <svg
              v-else
              class="w-4 h-4 mr-2 text-gray-300 dark:text-gray-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            /></svg>
            A symbol (#$&)
          </li>
          <li class="flex items-center">
            <svg
              v-if="passwordStrength.length >= 12"
              class="w-4 h-4 mr-2 text-primary-300 dark:text-primary-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            /></svg>
            <svg
              v-else
              class="w-4 h-4 mr-2 text-gray-300 dark:text-gray-400"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            ><path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            /></svg>
            A longer password (min. 12 chars.)
          </li>
        </ul>
      </div>
      <div data-popper-arrow />
    </div>
  </div>
</template>

<script>
  export default {
    emits: {'openLogin': null},
    data() {
      return {
        modal: null,
        loading: false,
        passwordStrength: {
          score: 0,
          length: 0,
          special: false,
          upperLower: false,
          number: false,
        }
      }
    },
    mounted() {
      this.modal = new Modal(document.querySelector('#register-modal'));
    },
    methods: {
      passwordStrengthCheck() {
        const password = document.querySelector('#registerPassword').value;
        let score = 0;
        // if password has more than 11 characters give 1 point
        if (password.length >= 12) score++;
        // if password has both lower and uppercase characters give 1 point
        if (password.match(/([a-z].*[A-Z])|([A-Z].*[a-z])/)) {
          this.passwordStrength.upperLower = true;
          score++
        }
        else this.passwordStrength.upperLower = false;
        // if password has at least one number give 1 point
        if (password.match(/([0-9])/)) {
          this.passwordStrength.number = true;
          score++
        }
        else this.passwordStrength.number = false;
        // if password has at least one special character give 1 point
        if (password.match(/([!,%,&,@,#,$,^,*,?,_,~])/)) {
          this.passwordStrength.special = true;
          score++
        }
        else this.passwordStrength.special = false;

        const length = password.length;
        this.passwordStrength.score = score;
        this.passwordStrength.length = length;
      },
      async submit() {
        this.loading = true;

        await this.$store.dispatch('api/register', {
          email: document.querySelector('#registerEmail').value,
          password: document.querySelector('#registerPassword').value,
          retypePassword: document.querySelector('#registerRetypePassword').value
        });

        this.loading = false;
      },
      showModal() {
        this.modal.show();
        document.querySelector('#registerEmail').focus();
      },
      closeModal() {
        this.modal.hide();
      },
      openLoginModal() {
        this.closeModal();
        this.$emit('openLogin');
      }
    }
  }
</script>
